import { useState } from 'react';
import { Search, Shield } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue }
  from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { usePerfisLista, usePermissoesPerfil }
  from '@/features/permissoes/hooks/usePermissoesPerfil';
import { TabelaPermissoes }
  from '@/features/permissoes/components/TabelaPermissoes';
import type { PermissaoFormulario } from '@/features/permissoes/types';

const normalizar = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export default function ConfiguracaoPerfil() {
  const [perfilId, setPerfilId] = useState<number | null>(null);

  const { perfis }                                       = usePerfisLista();
  const { gruposPorMenu, isLoading, salvarPermissao, salvarPorMenu }
                                                         = usePermissoesPerfil(perfilId);

  // Busca de tela atravessa todos os menus (o admin não precisa saber em qual aba está).
  const [buscaTela, setBuscaTela] = useState('');
  const termo = normalizar(buscaTela.trim());
  const gruposFiltrados = termo
    ? gruposPorMenu
        .map(g => ({ ...g, formularios: g.formularios.filter(f => normalizar(f.nomeFormulario).includes(termo)) }))
        .filter(g => g.formularios.length > 0)
    : gruposPorMenu;

  function handleAlterarPermissao(
    formularioId: number,
    campo: keyof Pick<PermissaoFormulario, 'podeLer' | 'podeIncluir' | 'podeEditar' | 'podeExcluir'>,
    valor: boolean
  ) {
    const formulario = gruposPorMenu
      .flatMap(g => g.formularios)
      .find(f => f.formularioId === formularioId);
    if (!formulario) return;

    salvarPermissao({
      formularioId,
      permissao: { ...formulario, [campo]: valor },
    });
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-2">
        <Shield className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-bold">Configuração de Perfis de Acesso</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Perfil</CardTitle>
        </CardHeader>
        <CardContent>
          <Select
            value={perfilId?.toString()}
            onValueChange={v => setPerfilId(Number(v))}
          >
            <SelectTrigger className="w-72">
              <SelectValue placeholder="Selecione um perfil..." />
            </SelectTrigger>
            <SelectContent>
              {perfis.map(p => (
                <SelectItem key={p.id} value={p.id.toString()}>
                  {p.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {perfilId && (
        <Card>
          <CardContent className="pt-4">
            {isLoading ? (
              <div className="flex justify-center py-10">
                <div className="h-8 w-8 animate-spin rounded-full
                                border-4 border-primary border-t-transparent" />
              </div>
            ) : (
              <>
              <div className="relative max-w-sm mb-4">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-8"
                  placeholder="Buscar tela em todos os menus..."
                  value={buscaTela}
                  onChange={e => setBuscaTela(e.target.value)}
                />
              </div>
              {termo ? (
                <div className="space-y-6">
                  {gruposFiltrados.length === 0 && (
                    <p className="text-sm text-muted-foreground py-6 text-center">
                      Nenhuma tela encontrada para "{buscaTela}".
                    </p>
                  )}
                  {gruposFiltrados.map(g => (
                    <div key={g.menuId}>
                      <h3 className="text-sm font-semibold mb-1">{g.nomeMenu}</h3>
                      <TabelaPermissoes
                        grupo={g}
                        semMarcarTudo
                        onToggleTudo={(menuId, concederTudo) =>
                          salvarPorMenu({ menuId, concederTudo })}
                        onAlterarPermissao={handleAlterarPermissao}
                      />
                    </div>
                  ))}
                </div>
              ) : (
              <Tabs defaultValue={gruposPorMenu[0]?.menuId.toString()}>
                <TabsList className="flex-wrap h-auto gap-1 mb-4">
                  {gruposPorMenu.map(g => (
                    <TabsTrigger key={g.menuId} value={g.menuId.toString()}>
                      {g.nomeMenu}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {gruposPorMenu.map(g => (
                  <TabsContent key={g.menuId} value={g.menuId.toString()}>
                    <TabelaPermissoes
                      grupo={g}
                      onToggleTudo={(menuId, concederTudo) =>
                        salvarPorMenu({ menuId, concederTudo })}
                      onAlterarPermissao={handleAlterarPermissao}
                    />
                  </TabsContent>
                ))}
              </Tabs>
              )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
